import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../app/store";
import {
    allOrientationsOfTiles, canPlayTileIn, getPlayableTile,
    getReasonCannotPlay,
    makeTiles,
    MaybeOrientedTile,
    OrientedTile,
    rotated,
    RotationType
} from "../../models/tile";
import {MAX_TILES_ON_RACK} from "../rack/Rack";

const makeEmptyRows = (boardSize: number) => {
    const rows = []
    for (let i = 0; i < boardSize; i++) {
        rows.push((new Array(boardSize)).fill({}));
    }
    return rows;
}


type BoardCellState = {
    tile?: OrientedTile,
};
type BoardSate = {
    rows: BoardCellState[][],
}

type GameState = {
    currentPlayer: Player | null,
    hoveredTile?: TileLocation,
    chosenTile?: TileLocation,
    racks: {
        tiles: OrientedTile[],
    }[]
    bag: {
        tiles: OrientedTile[],
    },
    board: BoardSate,
};

const initialState: GameState = {
    currentPlayer: null,
    racks: [],
    bag: {
        tiles: [],
    },
    board: {
        rows: [],
    },
}

export type Player = 0 | 1;

const takeRandomItem = <X>(items: X[]): X => {
    const randomIndex = Math.floor(Math.random() * items.length);
    return items.splice(randomIndex, 1)[0];
}


export type RackTileLocation = {
    kind: 'rack',
    player: Player,
    index: number,
}
export type BoardTileLocation = {
    kind: 'board',
    boardCol: number,
    boardRow: number,
}
export type TileLocation = RackTileLocation | BoardTileLocation;


type RequestToPlayTile = {
    source: TileLocation,
    boardCol: number,
    boardRow: number,
}


type MaybeCoordinate = {rowIndex: number, colIndex: number} | null

const rowsOfTiles = (board: BoardSate, exclude: MaybeCoordinate): MaybeOrientedTile[][] => {
    return board.rows.map((row, y) => row.map((cell, x) => {
        if (exclude && exclude.rowIndex === y && exclude.colIndex === x) {
            return undefined;
        }
        return cell.tile
    }));
}


const selectTileGivenLocation = (gameState: GameState, location: TileLocation): MaybeOrientedTile => {
    if (location.kind === "rack") {
        const player = location.player;
        if (player !== gameState.currentPlayer) {
            console.warn(`It is ${gameState.currentPlayer}'s turn, not ${player}'s.`);
        }
        return gameState.racks[player].tiles[location.index];
    } else {
        const {boardRow, boardCol} = location;
        const cell = gameState.board.rows[boardRow][boardCol];
        if ( ! cell.tile ) {
            console.warn(`No tile found at ${boardCol}, ${boardRow}`);
            return undefined;
        }
        return cell.tile;
    }
}

const setTileAtSource = (gameState: GameState, source: TileLocation, tile: MaybeOrientedTile) => {
    if (source.kind ===  "rack") {
        const {player, index} = source;
        const playerRack = gameState.racks[player];
        const tiles = playerRack.tiles;
        if (tile) {
            tiles.splice(index, 1, tile);
        } else {
            tiles.splice(index, 1);
        }
    } else {
        const { boardCol, boardRow } = source;
        gameState.board.rows[boardRow][boardCol].tile = tile;
    }
    if (tile) {
        gameState.hoveredTile = source;
    }
}


const getExcludeFromTileLocation = (source: TileLocation) => {
    return (source.kind === "board") ? {colIndex: source.boardCol, rowIndex: source.boardRow} : null;
}


const tryToPlayTile = (gameState: GameState, {source, boardCol, boardRow}: RequestToPlayTile): boolean => {
    if ( gameState.board.rows[boardRow][boardCol].tile ) {
        console.debug(`Target cell ${boardCol}, ${boardRow} is not empty`)
        return false;
    }
    const sourceTile = selectTileGivenLocation(gameState, source);
    if ( ! sourceTile ) {
        console.debug(`Could not find tile at source ${source}`);
        return false;
    }
    const exclude = getExcludeFromTileLocation(source);
    const playableTile = getPlayableTile(sourceTile, rowsOfTiles(gameState.board, exclude), boardCol, boardRow);
    if ( ! playableTile ) {
        console.debug(`Cannot play ${sourceTile} at ${boardCol}, ${boardRow}`);
        return false;
    }
    gameState.hoveredTile = undefined;
    gameState.chosenTile = undefined;
    setTileAtSource(gameState, source, undefined);
    setTileAtSource(gameState, {kind: "board", boardRow, boardCol}, playableTile);
    return true;
}


function findLegalCoordinates(board: BoardSate, tiles: OrientedTile[], exclude: MaybeCoordinate){
    const existingTiles = rowsOfTiles(board, exclude);
    tiles = allOrientationsOfTiles(tiles);
    return board.rows.map(
        (row, rowIndex) => row.map(
            (cell, colIndex) => tiles.some(
                tile => getReasonCannotPlay(tile, existingTiles, colIndex, rowIndex) === 'none'
            )
        )
    );
}


const rotateTileAtLocation = (state: GameState, location: TileLocation, rotationType: RotationType) => {
    const tile = selectTileGivenLocation(state, location);
    if (!tile) {
        console.warn(`Found no tile at ${location}`);
        return;
    }
    const rotatedTile = rotated(tile, rotationType)
    if (location.kind === "board") {
        const {boardCol: colIndex, boardRow: rowIndex} = location;
        const cellsExcludingThisOne = rowsOfTiles(state.board, {colIndex, rowIndex});
        if ( ! canPlayTileIn(rotatedTile, cellsExcludingThisOne, colIndex, rowIndex)) {
            return;
        }
    }
    setTileAtSource(state, location, rotated(tile, rotationType));
}


export const gameSlice = createSlice({
    'name': 'game',
    initialState,
    reducers: {
        initialise: (state, action: PayloadAction<{
            boardSize: number,
            numberOfValues: number,
        }>) => {
            const {boardSize, numberOfValues} = action.payload;
            state.currentPlayer = 0 as Player;
            state.bag = {tiles: makeTiles(numberOfValues)};
            state.racks = [{tiles: []}];
            state.board = {
                rows: makeEmptyRows(boardSize)
            };
        },
        pickTile: (state, action: PayloadAction<Player>) => {
            const player = action.payload;
            const bagTiles = state.bag.tiles;
            const tile = takeRandomItem(bagTiles);
            const rackTiles = state.racks[player].tiles;
            if (tile) {
                rackTiles.push(tile);
                if (rackTiles.length > MAX_TILES_ON_RACK) {
                    const tileToReturnToBag = rackTiles.shift();
                    if (tileToReturnToBag) {
                        state.bag.tiles.push(tileToReturnToBag);
                    }
                }
            }
        },
        playTile: (state, action: PayloadAction<RequestToPlayTile>) => {
            tryToPlayTile(state, action.payload);
        },
        boardToRack: (state, action: PayloadAction<{
            player: Player,
            boardCol: number,
            boardRow: number
        }>) => {
            const {player, boardCol, boardRow} = action.payload;
            const rack = state.racks[player];
            if (rack.tiles.length < MAX_TILES_ON_RACK) {
                const tile = state.board.rows[boardRow][boardCol]?.tile;
                if (tile) {
                    state.board.rows[boardRow][boardCol].tile = undefined;
                    const index = rack.tiles.push(tile) - 1;
                    state.hoveredTile = {
                        kind: "rack",
                        player,
                        index,
                    }
                }
            }
        },
        rotateTile: (state, action: PayloadAction<{
            location: TileLocation,
            rotationType: RotationType,
        }>) => {
            const {location, rotationType} = action.payload;
            rotateTileAtLocation(state, location, rotationType);
        },
        focusOnTile:  (state, action: PayloadAction<TileLocation>) => {
            state.hoveredTile = action.payload;
        },
        blurTile: (state) => {
            state.hoveredTile = undefined;
        },
        chooseTile: (state, action: PayloadAction<TileLocation>) => {
            state.chosenTile = action.payload;
        },
        swipeRotateTile: (state, action: PayloadAction<RotationType>) => {
            const location = state.chosenTile;
            if ( location ) {
                rotateTileAtLocation(state, location, action.payload);
            }
        },
    }
})

export const { initialise, pickTile, playTile, rotateTile, boardToRack, focusOnTile, blurTile, chooseTile, swipeRotateTile} = gameSlice.actions;

export type BoardCellContents = BoardCellState & {
    highlighted: boolean,
    isFocussed: boolean,
    isChosen: boolean,
    rotationTypes?: RotationType[],
}

const locationMatchesBoard = (colIndex: number, rowIndex: number, tileLocation?: TileLocation): boolean => {
    if ( tileLocation?.kind !== "board" ) {
        return false;
    }
    return (
        tileLocation.boardCol === colIndex &&
        tileLocation.boardRow === rowIndex
    );
}

const rackIndexFromLocation = (player: Player, tileLocation?: TileLocation): number | void => {
    if (tileLocation?.kind === "rack" && tileLocation.player === player) {
        return tileLocation.index;
    }
}

export const selectBoardState = (state: RootState): {
    size: number,
    rows: BoardCellContents[][]
} => {
    const game = state.game;
    const board = {...game.board};
    let tiles: OrientedTile[] = [];
    let exclude: MaybeCoordinate = null;
    if (game.hoveredTile) {
        const hoveredTile = selectTileGivenLocation(state.game, game.hoveredTile);
        if (hoveredTile) {
            tiles.push(hoveredTile);
        }
        exclude = getExcludeFromTileLocation(game.hoveredTile);
    } else {
        const rack = game.racks[game.currentPlayer as Player];
        if (rack) {
            tiles.push(...Array.from(rack.tiles))
        }
    }
    const highlights = findLegalCoordinates(board, tiles, exclude)
    const baseRotationTypes: RotationType[] = [
        "rotateClockwise",
        "flipVertical",
        "flipHorizontal"
    ]
    return {
        size: board.rows.length,
        rows: board.rows.map((row, rowIndex) => row.map((cell, colIndex) => {
            const cellContents: BoardCellContents = {
                ...cell,
                isFocussed: locationMatchesBoard(colIndex, rowIndex, game.hoveredTile),
                isChosen: locationMatchesBoard(colIndex, rowIndex, game.chosenTile),
                highlighted: highlights[rowIndex][colIndex],
            };
            // We only need the neighbouring cells for this (to check allowed rotation types)
            const tile = cell.tile;
            if (tile) {
                const cellsExcludingThisOne = rowsOfTiles(board, {rowIndex, colIndex});
                cellContents.rotationTypes = tile && baseRotationTypes.filter(rotationType => canPlayTileIn(
                    rotated(tile, rotationType),
                    cellsExcludingThisOne,
                    colIndex,
                    rowIndex,
                ));
            }
            return cellContents;
        }))
    }
}

export const selectBoardSizeNonzero = (state: RootState) => state.game.board.rows.length > 0;

export const selectBagIsEmpty = (state: RootState) => ! state.game.bag.tiles.length;

export const selectRackContents = (state: RootState) => {
    const {racks, hoveredTile, chosenTile} = state.game;
    return racks.map((rack, player) => ({
        ...rack,
        focussedTileIndex: rackIndexFromLocation(player as Player, hoveredTile),
        chosenTileIndex: rackIndexFromLocation(player as Player, chosenTile),
    }));
}

export const selectChosenTileLocation = (state: RootState) => {
    return state.game.chosenTile;
}

export default gameSlice.reducer;